import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { personalInfo, navSections } from '../../data/portfolioData';
import useScrollSpy from '../../hooks/useScrollSpy';
import { scrollToSection, scrollToTop } from '../../utils/scrollTo';
import { menuItem, menuPanel } from '../../motion/variants';

const Hamburger = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const activeSection = useScrollSpy(navSections.map((s) => s.id), 140);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open]);

  const closeMenu = () => setOpen(false);

  const goTo = (id) => {
    closeMenu();
    scrollToSection(id);
  };

  const navClass = (sectionId) => {
    const base = 'px-2.5 xl:px-3 py-2 text-sm font-medium transition-all duration-300 rounded-md whitespace-nowrap';
    if (activeSection === sectionId) {
      return `${base} text-white bg-white/[0.06] nav-active-indicator`;
    }
    return `${base} text-white/50 hover:text-white/80`;
  };

  return (
    <>
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed w-full top-0 left-0 z-50 transition-all duration-300 ${
          scrolled || open
            ? 'ant-glass bg-ant-bg/85 border-b border-ant-line-soft shadow-[0_8px_32px_rgba(0,0,0,0.35)]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 lg:h-[4.5rem]">
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => { closeMenu(); scrollToTop(); }}
              className="flex items-center gap-2 sm:gap-2.5 group min-h-[44px]"
              aria-label="Scroll to top"
            >
              <div className="w-8 h-8 rounded-lg border border-white/10 bg-white/[0.04] flex items-center justify-center">
                <span className="font-mono text-[11px] font-bold text-ant-gold">GS</span>
              </div>
              <span className="text-sm font-semibold text-white/90 group-hover:text-white transition-colors hidden xs:inline">
                Griffins
              </span>
            </motion.button>

            <nav className="hidden md:block overflow-x-auto scrollbar-none">
              <ul className="flex items-center gap-0.5">
                {navSections.map((item) => (
                  <li key={item.id}>
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={() => goTo(item.id)}
                      className={navClass(item.id)}
                    >
                      {item.label}
                    </motion.button>
                  </li>
                ))}
                <li className="ml-2 xl:ml-3 shrink-0">
                  <motion.a
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    href={personalInfo.resumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 xl:px-5 py-2 text-sm font-medium rounded-full bg-white text-ant-bg-2 hover:bg-white/90 transition-all duration-300"
                  >
                    Resume
                  </motion.a>
                </li>
              </ul>
            </nav>

            <motion.button
              whileTap={{ scale: 0.92 }}
              className="md:hidden p-2.5 text-white/70 hover:text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              onClick={() => setOpen(!open)}
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
            >
              <div className="w-6 h-6 flex flex-col justify-center items-center">
                <span className={`block w-5 h-px bg-white transition-all duration-300 ${open ? 'rotate-45 translate-y-0.5' : ''}`} />
                <span className={`block w-5 h-px bg-white transition-all duration-300 ${open ? 'opacity-0' : 'my-1.5'}`} />
                <span className={`block w-5 h-px bg-white transition-all duration-300 ${open ? '-rotate-45 -translate-y-0.5' : ''}`} />
              </div>
            </motion.button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={menuPanel}
              className="md:hidden overflow-hidden px-4 bg-ant-bg/98 backdrop-blur-xl border-t border-ant-line-soft"
            >
              <ul className="grid grid-cols-2 gap-2 py-5 max-h-[70vh] overflow-y-auto">
                {navSections.map((item) => (
                  <motion.li key={item.id} variants={menuItem}>
                    <button
                      onClick={() => goTo(item.id)}
                      className={`w-full text-left px-4 py-3.5 rounded-xl text-sm font-medium transition-colors min-h-[48px] ${
                        activeSection === item.id
                          ? 'text-white bg-white/[0.08] border border-ant-gold/20'
                          : 'text-white/50 hover:text-white border border-transparent'
                      }`}
                    >
                      {item.label}
                    </button>
                  </motion.li>
                ))}
              </ul>
              <motion.a
                variants={menuItem}
                href={personalInfo.resumeUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMenu}
                className="flex items-center justify-center w-full mb-5 px-4 py-3.5 text-sm font-medium text-ant-bg-2 bg-white rounded-full min-h-[48px]"
              >
                Download Resume
              </motion.a>
            </motion.nav>
          )}
        </AnimatePresence>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
            onClick={closeMenu}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Hamburger;